import { PATHS } from '@/routes/paths'
import { absoluteUrl, truncateMetaDescription, uniqueKeywords } from '../utils'
import {
  breadcrumbSchema,
  contactPageSchema,
  localBusinessSchema,
  organizationSchema,
  webApplicationSchema,
  webPageSchema,
} from '../schemas/common'
import type { SeoAdminFields } from '../types'

export interface StaticSeoInput {
  path: string
  title: string
  description: string
  keywords?: string
  admin?: SeoAdminFields | null
  schema?: Record<string, unknown> | Record<string, unknown>[]
}

export function buildStaticSeo(input: StaticSeoInput) {
  return {
    title: input.title,
    description: truncateMetaDescription(input.description),
    keywords: input.keywords,
    canonical: absoluteUrl(input.path),
    schema: input.schema,
  }
}

export const STATIC_PAGE_SEO = {
  about: buildStaticSeo({
    path: PATHS.about,
    title: 'About Quick Build Con',
    description:
      'Learn about Quick Build Con (QBC) — 80K+ app downloads, 25K+ active users and 5000+ verified properties across India.',
    keywords: 'about qbc, quick build con, real estate company, property platform india, 80k downloads',
    schema: [organizationSchema(), localBusinessSchema(), breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'About' }])],
  }),
  contact: buildStaticSeo({
    path: PATHS.contact,
    title: 'Contact Quick Build Con',
    description:
      'Contact Quick Build Con in Rajkot: +91 99041 56561, quickbuildcon@gmail.com, A-505 Nana Mava Rd, Karan Park. Listings, site visits and support.',
    keywords: 'contact qbc, qbc rajkot, property support, real estate help',
    schema: [
      localBusinessSchema(),
      contactPageSchema(),
      breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Contact' }]),
    ],
  }),
  faq: buildStaticSeo({
    path: PATHS.faq,
    title: 'Frequently Asked Questions | Quick Build Con',
    description:
      'Find answers about buying, selling, renting and listing properties on QBC. FAQs on verification, site visits, payments and account support.',
    keywords: 'property faq, qbc help, real estate questions',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'FAQ' }]),
  }),
  feedback: buildStaticSeo({
    path: PATHS.feedback,
    title: 'Share Your Feedback | Quick Build Con',
    description: 'Tell us about your experience with Quick Build Con. Your feedback helps us improve listings, support and platform features.',
    keywords: 'qbc feedback, customer review',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Feedback' }]),
  }),
  achievements: buildStaticSeo({
    path: PATHS.achievements,
    title: 'Our Achievements | Quick Build Con',
    description: 'Explore milestones and achievements of Quick Build Con in real estate technology, verified listings and customer success.',
    keywords: 'qbc achievements, real estate milestones',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Achievements' }]),
  }),
  privacy: (title: string, description: string) =>
    buildStaticSeo({
      path: PATHS.privacy,
      title,
      description,
      keywords: 'privacy policy, qbc data protection',
      schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: title }]),
    }),
  terms: (title: string, description: string) =>
    buildStaticSeo({
      path: PATHS.terms,
      title,
      description,
      keywords: 'terms and conditions, qbc terms',
      schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: title }]),
    }),
  notFound: {
    title: 'Page Not Found | Quick Build Con',
    description: 'The page you are looking for could not be found on Quick Build Con.',
    canonical: absoluteUrl('/404'),
    noIndex: true,
    noFollow: true,
  },
  privateDashboard: {
    title: 'My Account | Quick Build Con',
    description: 'Manage your QBC account, saved properties and listings.',
    noIndex: true,
    noFollow: true,
  },
  postProperty: buildStaticSeo({
    path: PATHS.addProperty,
    title: 'Post Property Free | Quick Build Con',
    description:
      'List your residential or commercial property on QBC for free. Reach verified buyers and tenants across India with zero brokerage listings.',
    keywords: 'post property free, list property, sell property online, qbc listing',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Post Property' }]),
  }),
  premiumProducts: buildStaticSeo({
    path: PATHS.premium,
    title: 'QBC Premium Marketing Packages | Sell Property Faster',
    description:
      'Promote listings with QBC Premium Verified packages: featured placement, photography and seller support across India. This is not a property search page.',
    keywords: 'qbc premium, property marketing packages, verified promotion, sell property faster',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'QBC Premium' }]),
  }),
  calculator: buildStaticSeo({
    path: PATHS.calculator,
    title: 'Cost Estimator | Construction Cost Calculator | QBC',
    description:
      'Estimate construction material costs by built-up area (sq.ft.) and convert land area units with the QBC Cost Estimator.',
    keywords: 'cost estimator, construction cost calculator, material cost, area unit converter, qbc calculator',
    schema: [
      webApplicationSchema({
        name: 'QBC Cost Estimator',
        path: PATHS.calculator,
        description:
          'Estimate construction material costs by built-up area (sq.ft.) and convert land area units with the QBC Cost Estimator.',
      }),
      breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Cost Estimator' }]),
    ],
  }),
  realEstateBook: buildStaticSeo({
    path: PATHS.realEstateBook,
    title: 'Real Estate Information | Guide Book | QBC',
    description:
      'Browse QBC Real Estate Information links, sample flip-book and guides for property buyers and professionals.',
    keywords: 'real estate information, real estate book, property guide, qbc real estate information',
    schema: [
      webPageSchema({
        name: 'Real Estate Information',
        path: PATHS.realEstateBook,
        description:
          'Browse QBC Real Estate Information links, sample flip-book and guides for property buyers and professionals.',
      }),
      breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Real Estate Information' }]),
    ],
  }),
  homeLoanEmi: () =>
    buildStaticSeo({
      path: PATHS.homeLoanEmi,
      title: 'Home Loan EMI Calculator | Quick Build Con',
      description:
        'Calculate your home loan EMI instantly. Plan loan amount, interest rate and tenure — then save details for QBC loan assistance.',
      keywords: 'home loan emi calculator, housing loan emi, qbc loan calculator',
      schema: [
        webApplicationSchema({
          name: 'QBC Home Loan EMI Calculator',
          path: PATHS.homeLoanEmi,
          description:
            'Calculate your home loan EMI instantly. Plan loan amount, interest rate and tenure — then save details for QBC loan assistance.',
          applicationCategory: 'FinanceApplication',
        }),
        breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Home Loan EMI' }]),
      ],
    }),
  homeLoanEligibility: () =>
    buildStaticSeo({
      path: PATHS.homeLoanEligibility,
      title: 'Home Loan Eligibility Calculator | Quick Build Con',
      description:
        'Check your home loan eligibility based on income, existing EMIs, credit score and property value. Get max loan amount and recommendations.',
      keywords: 'home loan eligibility, loan eligibility calculator, housing loan check',
      schema: [
        webApplicationSchema({
          name: 'QBC Home Loan Eligibility Calculator',
          path: PATHS.homeLoanEligibility,
          description:
            'Check your home loan eligibility based on income, existing EMIs, credit score and property value. Get max loan amount and recommendations.',
          applicationCategory: 'FinanceApplication',
        }),
        breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Home Loan Eligibility' }]),
      ],
    }),
  magazineList: buildStaticSeo({
    path: PATHS.magazine,
    title: 'QBC E-Magazine | Real Estate Insights',
    description:
      'Read QBC digital magazine editions covering property trends, market insights, builder stories and investment guides across India.',
    keywords: 'qbc magazine, real estate ebook, property magazine india',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Magazine' }]),
  }),
  coursesList: buildStaticSeo({
    path: PATHS.premiumCourseList,
    title: 'Digital Courses | Quick Build Con',
    description:
      'QBC premium digital courses for real estate professionals — learn marketing, sales and listing skills with expert-led training.',
    keywords: 'qbc courses, real estate training, property marketing course',
    schema: breadcrumbSchema([{ name: 'Home', url: PATHS.home }, { name: 'Courses' }]),
  }),
} as const

export function buildPrivateDashboardSeo(pageTitle: string) {
  return {
    title: pageTitle,
    description: STATIC_PAGE_SEO.privateDashboard.description,
    noIndex: true as const,
    noFollow: true as const,
  }
}

export function buildPremiumProductSeo(name: string, description: string, id: number | string) {
  return buildStaticSeo({
    path: PATHS.premiumProduct(id),
    title: `${name} | QBC Premium`,
    description: truncateMetaDescription(description),
    keywords: uniqueKeywords([name.toLowerCase(), 'qbc premium', 'property marketing package']),
    schema: breadcrumbSchema([
      { name: 'Home', url: PATHS.home },
      { name: 'Premium', url: PATHS.premium },
      { name: name },
    ]),
  })
}

export function buildMagazineDetailSeo(title: string, excerpt: string, slug: string) {
  return buildStaticSeo({
    path: PATHS.magazineDetail(slug),
    title: `${title} | QBC E-Magazine`,
    description: truncateMetaDescription(excerpt),
    keywords: uniqueKeywords([title.toLowerCase(), 'qbc magazine', 'real estate ebook']),
    schema: breadcrumbSchema([
      { name: 'Home', url: PATHS.home },
      { name: 'Magazine', url: PATHS.magazine },
      { name: title },
    ]),
  })
}
